import type { Identifier } from "ra-core";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { Ticket, TicketMessage } from "@/modules/types";
import {
  getContactEmail,
  getContactFullName,
} from "@/modules/clients/clientShowUtils";
import { resolveCompanyEmailForDisplay } from "@/modules/clients/companyChannelResolvers";
import { DEFAULT_TICKET_INBOX_EMAIL } from "@/modules/tickets/ticketInboxConfig";
import {
  getTicketListMeta,
  resolveTicketPrimaryContactName,
} from "@/modules/tickets/ticketListMeta";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (value?: string | null) => {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed || !EMAIL_REGEX.test(trimmed)) return null;
  return trimmed;
};

export const buildTicketInboxEmailSet = (
  ticket: Pick<Ticket, "inbox_address">,
  inboxEmails: Array<string | null | undefined> = [],
) => {
  const emails = new Set<string>();
  for (const value of inboxEmails) {
    const normalized = normalizeEmail(value);
    if (normalized) emails.add(normalized);
  }
  const ticketInbox = normalizeEmail(ticket.inbox_address);
  if (ticketInbox) emails.add(ticketInbox);
  const fallbackInbox = normalizeEmail(DEFAULT_TICKET_INBOX_EMAIL);
  if (fallbackInbox) emails.add(fallbackInbox);
  return emails;
};

/** Default to the current linked client; use the sender for unlinked tickets. */
export const resolveTicketReplyRecipientEmail = ({
  ticket,
  company,
  contact,
  recentMessages,
  inboxEmails = [],
}: {
  ticket: Ticket;
  company?: Company | null;
  contact?: Contact | null;
  recentMessages: TicketMessage[];
  inboxEmails?: Array<string | null | undefined>;
}) => {
  // Replies go to whoever wrote the ticket while that address still belongs
  // to the linked contact; a corrected contact email wins. The account's
  // billing contact is never a reply default (only invoices use it).
  const inboxSetForRequester = buildTicketInboxEmailSet(ticket, inboxEmails);
  const ticketRequester = normalizeEmail(ticket.requester_email);
  if (contact) {
    const contactEmails = (contact.email_jsonb ?? [])
      .map((entry) => normalizeEmail(entry?.email))
      .filter(
        (email): email is string =>
          Boolean(email) && !inboxSetForRequester.has(email as string),
      );
    if (ticketRequester && contactEmails.includes(ticketRequester)) {
      return ticketRequester;
    }
    return contactEmails[0] ?? "";
  }
  if (!contact && !company && (ticket.contact_id || ticket.company_id))
    return "";
  const requester = normalizeEmail(
    resolveTicketRequesterEmail(ticket, company, contact),
  );
  const inboxSet = buildTicketInboxEmailSet(ticket, inboxEmails);
  if (contact || company || ticket.contact_id || ticket.company_id) {
    return requester && !inboxSet.has(requester) ? requester : "";
  }

  for (const message of recentMessages) {
    if (message.direction !== "inbound") continue;
    const from = normalizeEmail(message.from_email);
    if (!from || inboxSet.has(from)) continue;
    return from;
  }

  return requester && !inboxSet.has(requester) ? requester : "";
};

export const isTicketInboxRecipient = (
  email: string,
  ticket: Pick<Ticket, "inbox_address">,
  inboxEmails: Array<string | null | undefined> = [],
) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return false;
  return buildTicketInboxEmailSet(ticket, inboxEmails).has(normalized);
};

const normalize = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "—") return null;
  return trimmed;
};

export const resolveTicketRequesterEmail = (
  ticket: Ticket,
  company?: Company | null,
  contact?: Contact | null,
) => getTicketListMeta(ticket, company, contact).email;

export const resolveTicketRequesterName = (
  ticket: Ticket,
  contact?: Contact | null,
  company?: Company | null,
) => resolveTicketPrimaryContactName(ticket, company, contact);

export const getTicketClientFirstName = (
  ticket: Ticket,
  contact?: Contact | null,
  company?: Company | null,
) => {
  const name = resolveTicketRequesterName(ticket, contact, company);
  if (!name) return "there";
  const [firstName] = name.split(/\s+/);
  return firstName || name;
};

export const resolveRequesterFromContactAndCompany = (
  contact?: Contact | null,
  company?: Company | null,
) => {
  let email = contact ? normalize(getContactEmail(contact)) : null;
  let name = contact ? normalize(getContactFullName(contact)) : null;

  if (!email && company) {
    const companyEmail = resolveCompanyEmailForDisplay(company);
    if (companyEmail !== "—") email = companyEmail;
  }

  if (!name && company) {
    name = normalize(company.name);
  }

  return { email, name };
};

export const contactMatchesId = (
  contact: Contact,
  contactId: string | number | null | undefined,
) => contactId != null && String(contact.id) === String(contactId);

export const splitRequesterName = (name?: string | null) => {
  const trimmed = name?.trim();
  if (!trimmed) return { first_name: "", last_name: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { first_name: parts[0] ?? "", last_name: "" };
  }
  return {
    first_name: parts[0] ?? "",
    last_name: parts.slice(1).join(" "),
  };
};

/** Prefill values for ContactFormDialog when saving an inbound ticket sender as a CRM contact. */
export const buildContactCreateDefaultsFromTicket = (
  ticket: Ticket,
  companyId?: Identifier | null,
) => {
  const { first_name, last_name } = splitRequesterName(ticket.requester_name);
  const email = ticket.requester_email?.trim() ?? "";
  const resolvedCompanyId = companyId ?? ticket.company_id ?? null;

  return {
    first_name,
    last_name,
    company_id: resolvedCompanyId,
    email_jsonb: [{ email, type: "Work" as const }],
    phone_jsonb: [{ number: "", type: "Work" as const }],
  };
};

/**
 * Copies to keep on a reply: everyone on the client's last email (To + Cc)
 * except our inboxes and the main recipient — like "Reply all".
 */
export const resolveTicketReplyCcEmails = ({
  ticket,
  lastInbound,
  toEmail,
  inboxEmails = [],
}: {
  ticket: Pick<Ticket, "inbox_address">;
  lastInbound?: Pick<TicketMessage, "to_emails" | "cc_emails"> | null;
  toEmail?: string | null;
  inboxEmails?: Array<string | null | undefined>;
}) => {
  if (!lastInbound) return [];
  const inboxSet = buildTicketInboxEmailSet(ticket, inboxEmails);
  const main = normalizeEmail(toEmail);
  const seen = new Set<string>();
  return [...(lastInbound.to_emails ?? []), ...(lastInbound.cc_emails ?? [])]
    .flatMap((value) => String(value ?? "").split(/[,;]/))
    .map((value) => normalizeEmail(value))
    .filter((email): email is string => {
      if (!email || inboxSet.has(email) || email === main || seen.has(email)) {
        return false;
      }
      seen.add(email);
      return true;
    });
};
